import { useState } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Youtube, Facebook, Twitter, Send, ShieldCheck, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { NAV_LINKS, BRAND, SERVICES, CERTIFICATIONS } from "@/config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/newsletter`, { email });
      toast.success(res.data.message || "Subscribed!");
      setEmail("");
    } catch {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative z-10 border-t border-slate-800 bg-[#0B1120]">
      <div className="mx-auto max-w-7xl px-6 pt-16 pb-6 lg:px-10 lg:pt-20 lg:pb-8">
        {/* certifications strip */}
        <div className="mb-14 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-b border-slate-800 pb-10">
          {CERTIFICATIONS.map((c) => (
            <span key={c} className="inline-flex items-center gap-2 text-sm font-medium text-slate-400">
              <ShieldCheck className="h-4 w-4 text-[#2563EB]" /> {c}
            </span>
          ))}
        </div>

        <div className="grid gap-12 lg:grid-cols-12">
          {/* brand + contact */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-2.5">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#2563EB]">
                <ShieldCheck className="h-5 w-5 text-white" />
              </span>
              <span className="font-display text-lg font-extrabold tracking-tight text-white">
                KAS Info Tech (P) Ltd.
              </span>
            </Link>
            <p className="mt-5 max-w-sm text-base leading-relaxed text-slate-400">
              {BRAND.tagline}. Securing government and corporate infrastructure across India
              with proven, government-grade reliability.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-slate-400">
              <li><a href={`tel:${BRAND.phoneRaw}`} className="inline-flex items-center gap-2 transition-colors hover:text-white"><Phone className="h-4 w-4 text-[#2563EB]" /> {BRAND.phone}</a></li>
              <li><a href={`mailto:${BRAND.email}`} className="inline-flex items-center gap-2 transition-colors hover:text-white"><Mail className="h-4 w-4 text-[#2563EB]" /> {BRAND.email}</a></li>
              <li className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-[#2563EB]" /> {BRAND.address}</li>
            </ul>
          </div>

          {/* links */}
          <div className="lg:col-span-2">
            <p className="font-display text-sm font-bold uppercase tracking-widest text-slate-500">Company</p>
            <ul className="mt-4 space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} data-testid={`footer-link-${l.label.toLowerCase()}`} className="text-sm text-slate-400 transition-colors hover:text-white">{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="font-display text-sm font-bold uppercase tracking-widest text-slate-500">Services</p>
            <ul className="mt-4 space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services#${s.slug}`} className="text-sm text-slate-400 transition-colors hover:text-white">{s.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* newsletter */}
          <div className="lg:col-span-3">
            <p className="font-display text-sm font-bold uppercase tracking-widest text-slate-500">Newsletter</p>
            <p className="mt-4 text-sm text-slate-400">Security insights & product updates. No spam.</p>
            <form onSubmit={subscribe} className="mt-4 flex gap-2" data-testid="newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="newsletter-input"
                className="focus-ring w-full rounded-full border border-slate-700 bg-white/5 px-4 py-2.5 text-sm text-white placeholder:text-slate-500 outline-none"
              />
              <button type="submit" disabled={loading} data-testid="newsletter-submit" className="flex shrink-0 items-center justify-center rounded-full bg-[#2563EB] px-4 py-2.5 text-white transition-colors hover:bg-[#3b82f6] disabled:opacity-60">
                <Send className="h-4 w-4" />
              </button>
            </form>
            <div className="mt-6 flex items-center gap-3">
              {[
                { Icon: Linkedin, label: "linkedin", href: "https://linkedin.com" },
                { Icon: Youtube, label: "youtube", href: "https://youtube.com" },
                { Icon: Facebook, label: "facebook", href: "https://facebook.com" },
                { Icon: Twitter, label: "twitter", href: "https://twitter.com" },
              ].map(({ Icon, label, href }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" data-testid={`social-${label}`} aria-label={label} className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-700 text-slate-400 transition-colors hover:border-[#2563EB] hover:text-white">
                  <Icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-col items-center justify-between gap-4 border-t border-slate-800 pt-5 sm:flex-row text-sm text-slate-500">
          <p>© {new Date().getFullYear()} {BRAND.name}. All rights reserved.</p>
          <div className="flex gap-6">
            <Link to="/privacy" className="transition-colors hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="transition-colors hover:text-white">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
